import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Checkbox,
  Tooltip,
} from "antd";
import { useSelector } from "react-redux";
import { useGetBreakpoints } from "../../hooks/useGetBreakpoints";
import { labelComponent } from "../../util/UtilComponents";
import { InfoCircleOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import InfoPopup from "../../util/InfoPopup";

const { Option } = Select;

const PriceCalculatorModal = ({
  visible,
  onCancel,
  onSave,
  form,
  initialValues,
  isEditing,
  previousRowData,
  selectedCurrencies = ["INR"],
  items = [],
  setItems,
}) => {
  const countries = useSelector(({ common }) => common.countries);
  const { isMobile } = useGetBreakpoints();
  const productsUnitList = useSelector(
    ({ myProducts }) => myProducts.productsUnitList || []
  );
  const [showExchangeRate, setShowExchangeRate] = useState(false);
  const [showCustomGstRate, setShowCustomGstRate] = useState(false);
  const [gstType, setGstType] = useState("percentage");

  const uniqueProductsUnitList = Array.from(
    new Map(
      (productsUnitList || []).filter(Boolean).map((item) => [item.label, item])
    ).values()
  );

  const calculateAmount = (rate, units, gst, gstType, exchangeRate = 1) => {
    const handleExchangeRate = exchangeRate ?? 1;
    const subtotal = rate * units * handleExchangeRate;
    let gstAmount = 0;
    let total = subtotal;

    if (gstType === "percentage") {
      gstAmount = (subtotal * gst) / 100;
      total = subtotal + gstAmount;
    } else {
      gstAmount = gst;
      total = subtotal + gstAmount;
    }

    return { subtotal, gstAmount, total };
  };

  useEffect(() => {
    if (visible && form) {
      if (!isEditing && initialValues) {
        const isCustomRate = ![0, 0.25, 3, 5, 10, 12, 18, 28].includes(
          Number(initialValues.gstRate)
        );

        form.setFieldsValue({
          ...initialValues,
          gstRate: isCustomRate ? "others" : initialValues.gstRate,
          customGstRate: isCustomRate ? initialValues.gstRate : undefined,
        });
        setShowCustomGstRate(isCustomRate);
      }

      const initialCurrency = initialValues.currency || "INR";
      setShowExchangeRate(initialCurrency !== "INR");

      const isCustomRate = initialValues.gstRate === "others";
      const gstValue = isCustomRate
        ? initialValues.customGstRate || 0
        : initialValues.gstRate || 0;

      const { total } = calculateAmount(
        initialValues.ratePerUnit || 0,
        initialValues.noOfUnits || 0,
        gstValue,
        "percentage",
        initialValues.exchangeRate || 1
      );
      form.setFieldsValue({ amount: total || 0 });
    }
  }, [visible, initialValues, form, isEditing, previousRowData]);

  const handleValuesChange = (changedValues, allValues) => {
    if ("currency" in changedValues) {
      const shouldShow = changedValues.currency !== "INR";
      setShowExchangeRate(shouldShow);

      if (changedValues.currency === "INR") {
        form.setFieldsValue({ exchangeRate: 1 });
      } else {
        form.setFieldsValue({ exchangeRate: null });
      }
      form.validateFields(["exchangeRate"]);
    }

    if ("gstType" in changedValues) {
      setShowCustomGstRate(changedValues.gstRate === "others");
      setGstType(changedValues.gstType);
      form.setFieldsValue({ gstRate: 0, gstAmount: 0 });
    }

    if (
      "ratePerUnit" in changedValues ||
      "noOfUnits" in changedValues ||
      "gstRate" in changedValues ||
      "customGstRate" in changedValues ||
      "gstType" in changedValues ||
      "exchangeRate" in changedValues ||
      "currency" in changedValues
    ) {
      const values = form.getFieldsValue();
      const isCustomRate = values.gstRate === "others";
      const gstValue = isCustomRate
        ? values.customGstRate || 0
        : values.gstRate || 0;

      const { gstAmount, total } = calculateAmount(
        values.ratePerUnit || 0,
        values.noOfUnits || 0,
        gstValue,
        "percentage",
        values.exchangeRate || 1
      );

      form.setFieldsValue({
        gstAmount,
        amount: total,
      });
    }
  };
  const currency = Form.useWatch("currency", form);
  const handleSave = async () => {
    try {
      await form.validateFields();
      const values = form.getFieldsValue();
      onSave(values);
    } catch (error) {
      console.error("Validation failed:", error);
    }
  };

  return (
    <Modal
      title={
        <span style={{ fontWeight: "bold" }}>
          {isEditing ? "Edit Item" : "Add New Item"}
        </span>
      }
      open={visible}
      onOk={handleSave}
      onCancel={onCancel}
      okText="Save"
      cancelText="Cancel"
      width={550}
      className="custom-modal"
    >
      <Form
        form={form}
        layout="horizontal"
        initialValues={{
          ...initialValues,
          currency: initialValues.currency || "INR",
          exchangeRate: initialValues.currency === "INR" ? 1 : null,
          gstType: initialValues.gstType || "percentage",
          gstRate: initialValues.gstRate || 0,
          customGstRate: initialValues.customGstRate || undefined,
          ratePerUnit: null,
          gstAmount: 0,
          amount:
            typeof initialValues.amount === "number" ? initialValues.amount : 0,
          isPrimary:
            initialValues.isPrimary !== undefined
              ? initialValues.isPrimary
              : true,
        }}
        labelCol={{
          span: 8,
          style: { textAlign: "left", paddingRight: "8px" },
        }}
        wrapperCol={{
          span: 16,
        }}
        labelAlign="left"
        onValuesChange={handleValuesChange}
      >
        <Form.Item
          label={<span style={{ fontWeight: "bold" }}>Description</span>}
          name="description"
          rules={[{ required: true, message: "Please enter description" }]}
          required={false}
          colon={false}
          extra={
            !items.some((item) => item.isPrimary) && (
              <Form.Item name="isPrimary" valuePropName="checked" noStyle>
                <Checkbox
                  defaultChecked={true}
                  onChange={(e) => {
                    if (e.target.checked && setItems) {
                      const updatedItems = items.map((item) => ({
                        ...item,
                        isPrimary: false,
                      }));
                      setItems(updatedItems);
                    }
                  }}
                  style={{ marginTop: 8 }}
                >
                  Primary Item
                  <Tooltip title="Select primary item to calculate the price per unit">
                    <InfoCircleOutlined
                      style={{ marginLeft: 8, color: "#999", fontSize: "12px" }}
                    />
                  </Tooltip>
                </Checkbox>
              </Form.Item>
            )
          }
        >
          <Input placeholder="Enter description" />
        </Form.Item>

        <Form.Item
          label={<span style={{ fontWeight: "bold" }}>Currency</span>}
          name="currency"
          rules={[{ required: true, message: "Currency is required" }]}
          required={false}
          colon={false}
        >
          <Select
            showSearch
            size="large"
            placeholder="Select Currency"
            getPopupContainer={(trigger) => trigger.parentNode}
          >
            {[
              ...new Set([
                ...selectedCurrencies,
                ...(countries
                  ?.filter((country) => country.currencyCode)
                  .map((country) => country.currencyCode) || []),
              ]),
            ].map((currencyCode) => (
              <Option
                key={currencyCode}
                value={currencyCode}
                disabled={
                  selectedCurrencies.length >= 2 &&
                  !selectedCurrencies.includes(currencyCode)
                }
              >
                {currencyCode}
                {selectedCurrencies.length >= 2 &&
                  !selectedCurrencies.includes(currencyCode) &&
                  " (Not Allowed)"}
              </Option>
            ))}
          </Select>
        </Form.Item>

        {showExchangeRate && (
          <Form.Item
            label={
              <div>
                <span style={{ fontWeight: "bold" }}>Exchange Rate</span>
                <br />({form.getFieldValue("currency") || "___"} to INR)
              </div>
            }
            name="exchangeRate"
            dependencies={["currency"]}
            validateTrigger={["onChange", "onBlur"]}
            colon={false}
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value) {
                    return Promise.reject(
                      new Error("Exchange rate is required")
                    );
                  }
                  if (value <= 0) {
                    return Promise.reject(
                      new Error("Exchange rate must be greater than 0")
                    );
                  }
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <InputNumber
              style={{ width: "100%" }}
              min={0.0001}
              step={0.01}
              controls={false}
            />
          </Form.Item>
        )}
        <Form.Item
          label={<span style={{ fontWeight: "bold" }}>Rate Per Unit</span>}
          colon={false}
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            <Form.Item
              name="ratePerUnit"
              noStyle
              rules={[
                {
                  required: true,
                  message: "Please enter rate",
                  validator: (_, value) =>
                    value > 0
                      ? Promise.resolve()
                      : Promise.reject("Rate must be > 0"),
                },
              ]}
              required={false}
            >
              <InputNumber
                style={{
                  width: "100%",
                  marginRight: 8,
                }}
                // min={0}
                placeholder="Enter rate"
                step={0.01}
                formatter={(value) =>
                  value ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : ""
                }
                parser={(value) => value?.replace(/\$\s?|(,*)/g, "")}
                controls={false}
                addonBefore={!!currency ? currency : "INR"}
              />
            </Form.Item>
            <span style={{ margin: "0 8px", color: "#888" }}>Per</span>
            <Form.Item
              name="uom"
              noStyle
              rules={[
                {
                  required: true,
                  message: "Unit of Measurement(UOM) is required",
                },
              ]}
              required={false}
            >
              <Select
                showSearch
                placeholder="Select UOM"
                style={{ width: "100%" }}
                getPopupContainer={(trigger) => trigger.parentNode}
              >
                {uniqueProductsUnitList?.map((option) => (
                  <Option key={option.label} value={option.label}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </div>
        </Form.Item>

        <Form.Item
          label={<span style={{ fontWeight: "bold" }}>Quantity</span>}
          name="noOfUnits"
          colon={false}
        >
          <Input.Group
            compact
            style={{ display: "flex", alignItems: "stretch" }}
          >
            <Form.Item
              name="noOfUnits"
              noStyle
              rules={[
                {
                  required: true,
                  message: "Please enter quantity",
                  validator: (_, value) =>
                    value > 0
                      ? Promise.resolve()
                      : Promise.reject("Quantity must be given"),
                },
              ]}
              required={false}
            >
              <InputNumber
                style={{ flex: 1, minWidth: "50%" }}
                min={0}
                controls={false}
                placeholder="Enter Quantity"
              />
            </Form.Item>
            <Form.Item name="uom" noStyle>
              <Input
                style={{
                  flex: 1,
                  minWidth: "50%",
                  backgroundColor: "#f5f5f5",
                  color: "rgba(0, 0, 0, 0.85)",
                  cursor: "not-allowed",
                  height: "100%",
                }}
                disabled
                value={form.getFieldValue("uom")}
              />
            </Form.Item>
          </Input.Group>
        </Form.Item>

        <Form.Item
          label={<span style={{ fontWeight: "bold" }}>GST Rate (%)</span>}
          name="gstRate"
          rules={[{ required: true, message: "Please enter GST rate" }]}
          required={false}
          colon={false}
        >
          <Input.Group
            compact
            style={{ display: "flex", alignItems: "stretch" }}
          >
            <Form.Item name="gstRate" noStyle>
              <Select
                showSearch
                placeholder="Select GST Rate"
                style={{ flex: 1, minWidth: showCustomGstRate ? "30%" : "50%" }}
                onChange={(value) => {
                  setShowCustomGstRate(value === "others");
                  if (value !== "others") {
                    const values = form.getFieldsValue();
                    const { gstAmount } = calculateAmount(
                      values.ratePerUnit || 0,
                      values.noOfUnits || 0,
                      value || 0,
                      "percentage",
                      values.exchangeRate || 1
                    );
                    form.setFieldsValue({
                      gstAmount,
                      customGstRate: undefined,
                    });
                  }
                }}
              >
                <Option value={0}>0%</Option>
                <Option value={0.25}>0.25%</Option>
                <Option value={3}>3%</Option>
                <Option value={5}>5%</Option>
                <Option value={10}>10%</Option>
                <Option value={12}>12%</Option>
                <Option value={18}>18%</Option>
                <Option value={28}>28%</Option>
                <Option value="others">Others</Option>
              </Select>
            </Form.Item>

            {showCustomGstRate && (
              <Form.Item
                name="customGstRate"
                noStyle
                rules={[
                  {
                    required: showCustomGstRate,
                    message: "Please enter custom GST rate",
                  },
                  {
                    validator: (_, value) => {
                      if (value < 0) {
                        return Promise.reject("GST rate cannot be negative");
                      }
                      if (value > 100) {
                        return Promise.reject("GST rate cannot exceed 100%");
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <InputNumber
                  style={{ flex: 1, minWidth: "20%" }}
                  min={0}
                  max={100}
                  step={0.01}
                  placeholder="Custom %"
                  formatter={(value) => `${value}%`}
                  parser={(value) => value.replace("%", "")}
                  controls={false}
                  onChange={(value) => {
                    const values = form.getFieldsValue();
                    const { gstAmount } = calculateAmount(
                      values.ratePerUnit || 0,
                      values.noOfUnits || 0,
                      value || 0,
                      "percentage",
                      values.exchangeRate || 1
                    );
                    form.setFieldsValue({ gstAmount });
                  }}
                />
              </Form.Item>
            )}

            <Form.Item
              name="gstAmount"
              noStyle
              rules={[
                {
                  required: true,
                  message: "Please enter GST amount",
                },
                {
                  validator: (_, value) => {
                    if (value < 0) {
                      return Promise.reject("GST amount cannot be negative");
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <InputNumber
                style={{
                  flex: 1,
                  minWidth: showCustomGstRate ? "50%" : "50%",
                  height: "100%",
                }}
                min={0}
                step={0.01}
                placeholder="GST Amount"
                formatter={(value) =>
                  `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                }
                parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
                controls={false}
                disabled
              />
            </Form.Item>
          </Input.Group>
        </Form.Item>

        <Form.Item
          label={<span style={{ fontWeight: "bold" }}>Total Amount</span>}
          name="amount"
          labelCol={{ span: 8 }}
          wrapperCol={{ span: 16 }}
          colon={false}
        >
          <InputNumber
            style={{ width: "100%" }}
            disabled
            formatter={(value) =>
              `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
            }
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default PriceCalculatorModal;
